import { clampDescription, buildTitle } from "@/lib/seo-quality";
import { robotsMeta } from "@/lib/seo-quality";
import { assessComparison } from "@/lib/seo-families/comparisons";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { Phone, ArrowRight, Check } from "lucide-react";
import { AdSlot } from "@/components/AdSlot";
import { PageHeader } from "./fordon";
import { getComparison, COMPARISONS } from "@/data/husbil-comparisons";
import { getComparisonDjup } from "@/data/husbil-comparisons-djup";

export const Route = createFileRoute("/jamfor/$slug")({
  // Jämförelsedatan behövs bara här — grupperas med komponenten så den lämnar
  // initial-bundlen. head() läser därför bara färdiga värden från loadern.
  codeSplitGroupings: [["loader", "component"]],
  loader: ({ params }) => {
    const cmp = getComparison(params.slug);
    if (!cmp) throw notFound();
    const djup = getComparisonDjup(params.slug);
    const robots = robotsMeta(assessComparison(cmp));
    return { cmp, djup, robots };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const { cmp, djup } = loaderData;
    const faqItems = [...cmp.faq, ...(djup?.faq ?? [])];
    const url = `https://www.akhusbilfritid.se/jamfor/${cmp.slug}`;
    const title = buildTitle(`${cmp.aName} vs ${cmp.bName} — jämförelse 2026`);
    const desc = `${cmp.aName} eller ${cmp.bName}? Full jämförelse: pris, mått, isolering, för/nack. ${cmp.winner}`;
    return {
      meta: [
        ...loaderData.robots,
        { title },
        { name: "description", content: clampDescription(desc) },
        { property: "og:title", content: title },
        { property: "og:description", content: clampDescription(desc) },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqItems.map((f: { q: string; a: string }) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: `${cmp.aName} vs ${cmp.bName}`,
            description: cmp.intro,
            author: { "@type": "Organization", name: "AK Husbil & Fritid" },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Hem", item: "https://www.akhusbilfritid.se" },
              { "@type": "ListItem", position: 2, name: "Jämförelser", item: "https://www.akhusbilfritid.se/jamfor" },
              { "@type": "ListItem", position: 3, name: `${cmp.aName} vs ${cmp.bName}`, item: url },
            ],
          }),
        },

      ],
    };
  },
  notFoundComponent: () => (
    <div className="max-w-2xl mx-auto p-8 text-center">
      <h1 className="text-2xl font-bold mb-4">Jämförelsen hittades inte</h1>
      <Link to="/jamfor" className="text-primary underline">Se alla jämförelser</Link>
    </div>
  ),
  component: JamforPage,
});

function JamforPage() {
  const { cmp, djup } = Route.useLoaderData();
  return (
    <>
      <PageHeader
        label={cmp.category}
        title={<>{cmp.aName} <span className="text-muted-foreground">vs</span> {cmp.bName}</>}
        sub={cmp.winner}
      />
      <main className="max-w-4xl mx-auto px-4 py-10 space-y-10">
        <p className="text-lg leading-relaxed">{cmp.intro}</p>

        <section className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b-2">
                <th className="text-left py-3 px-2 font-semibold">Egenskap</th>
                <th className="text-left py-3 px-2 font-bold">{cmp.aName}</th>
                <th className="text-left py-3 px-2 font-bold">{cmp.bName}</th>
              </tr>
            </thead>
            <tbody>
              {cmp.rows.map((r: { label: string; a: string; b: string }) => (
                <tr key={r.label} className="border-b">
                  <td className="py-3 px-2 text-muted-foreground">{r.label}</td>
                  <td className="py-3 px-2">{r.a}</td>
                  <td className="py-3 px-2">{r.b}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <section className="grid md:grid-cols-2 gap-6">
          <div className="rounded-xl border p-6">
            <h3 className="font-bold text-lg mb-3">{cmp.aName} styrkor</h3>
            <ul className="space-y-2">
              {cmp.aStrengths.map((s: string) => (
                <li key={s} className="flex gap-2"><Check className="w-5 h-5 text-primary shrink-0 mt-0.5" /><span>{s}</span></li>
              ))}
            </ul>
          </div>
          <div className="rounded-xl border p-6">
            <h3 className="font-bold text-lg mb-3">{cmp.bName} styrkor</h3>
            <ul className="space-y-2">
              {cmp.bStrengths.map((s: string) => (
                <li key={s} className="flex gap-2"><Check className="w-5 h-5 text-primary shrink-0 mt-0.5" /><span>{s}</span></li>
              ))}
            </ul>
          </div>
        </section>

        <section className="rounded-xl border-2 border-primary/20 bg-muted/40 p-6">
          <h2 className="text-xl font-bold mb-2">Vår rekommendation</h2>
          <p>{cmp.verdict}</p>
        </section>

        {djup && (
          <>
            <section className="grid md:grid-cols-2 gap-6">
              <div className="rounded-xl border p-6">
                <h2 className="text-xl font-bold mb-3">Ägandekostnad över fem år</h2>
                <p className="text-muted-foreground leading-relaxed">{djup.agandekostnad}</p>
              </div>
              <div className="rounded-xl border p-6">
                <h2 className="text-xl font-bold mb-3">Andrahandsvärde</h2>
                <p className="text-muted-foreground leading-relaxed">{djup.andrahandsvarde}</p>
              </div>
            </section>

            <section className="rounded-xl border p-6">
              <h2 className="text-xl font-bold mb-3">Vem väljer vad?</h2>
              <p className="text-muted-foreground leading-relaxed">{djup.vemValjerVad}</p>
            </section>

            <section className="grid md:grid-cols-2 gap-6">
              <div className="rounded-xl border p-6">
                <h2 className="text-lg font-bold mb-3">Vanliga missar</h2>
                <ul className="space-y-2">
                  {djup.vanligaMissar.map((m: string) => (
                    <li key={m} className="flex gap-2 text-sm">
                      <span className="text-primary shrink-0">•</span>
                      <span className="text-muted-foreground">{m}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-xl border p-6">
                <h2 className="text-lg font-bold mb-3">Checklista vid provkörning</h2>
                <ul className="space-y-2">
                  {djup.provkorning.map((p: string) => (
                    <li key={p} className="flex gap-2 text-sm">
                      <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </section>

            <section className="rounded-xl border-2 border-primary/20 bg-muted/40 p-6">
              <h2 className="text-xl font-bold mb-2">Nordiskt vinterperspektiv</h2>
              <p className="leading-relaxed">{djup.nordisktPerspektiv}</p>
            </section>

            <section className="rounded-xl border p-6">
              <h2 className="text-xl font-bold mb-3">Verkstad och reservdelar</h2>
              <p className="text-muted-foreground leading-relaxed">{djup.verkstadOchDelar}</p>
            </section>
          </>
        )}

        <AdSlot variant="in-article" />

        <section>
          <h2 className="text-2xl font-bold mb-4">Vanliga frågor</h2>
          <div className="space-y-4">
            {[...cmp.faq, ...(djup?.faq ?? [])].map((f: { q: string; a: string }) => (
              <div key={f.q} className="rounded-lg border p-4">
                <div className="font-semibold mb-1">{f.q}</div>
                <div className="text-muted-foreground">{f.a}</div>
              </div>
            ))}
          </div>
        </section>

        <section>
          <h2 className="text-xl font-bold mb-3">Fler jämförelser</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {COMPARISONS.filter((c) => c.slug !== cmp.slug).slice(0, 6).map((c) => (
              <Link
                key={c.slug}
                to="/jamfor/$slug"
                params={{ slug: c.slug }}
                className="rounded-lg border p-4 hover:border-primary hover:bg-muted/40"
              >
                <div className="text-xs text-muted-foreground">{c.category}</div>
                <div className="font-semibold">{c.aName} vs {c.bName}</div>
              </Link>
            ))}
          </div>
        </section>

        <AdSlot variant="multiplex" label="Fler jämförelser" />


        <section className="rounded-xl bg-primary text-primary-foreground p-8 text-center">
          <h2 className="text-2xl font-bold mb-3">Osäker på vilken som passar dig?</h2>
          <p className="mb-6 opacity-90">Ring — vi hjälper igenom valet ärligt.</p>
          <a href="tel:0708629743" className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-background text-foreground font-semibold hover:opacity-90">
            <Phone className="w-4 h-4" /> 070-862 97 43
          </a>
        </section>
      </main>
    </>
  );
}
